import * as React from 'react';
import type { Editor } from '@tiptap/react';
import { Columns3Icon, MergeIcon, PanelTopIcon, PanelLeftIcon, Rows3Icon, SplitIcon, TableIcon, Trash2Icon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

type TableAction = { label: string; icon: React.ReactNode; can: () => boolean; command: () => boolean; destructive?: boolean };

const GRID_SIZE = 5;

/** Insert → Table: size grid plus row, column, cell and table operations (Solution Studio TableMenu parity). */
export function DocumentTableMenu({ editor, disabled }: { editor: Editor; disabled: boolean }) {
  const [open, setOpen] = React.useState(false);
  const [size, setSize] = React.useState({ rows: 2, cols: 2 });
  const unavailable = disabled || !editor.isEditable;
  const inTable = editor.isActive('table');
  const run = (command: () => boolean) => { if (!unavailable) command(); setOpen(false); };
  const chain = () => editor.chain().focus();
  const groups: ReadonlyArray<{ label: string; actions: TableAction[] }> = [
    { label: 'Rows and columns', actions: [
      { label: 'Add row above', icon: <Rows3Icon aria-hidden />, can: () => editor.can().addRowBefore(), command: () => chain().addRowBefore().run() },
      { label: 'Add row below', icon: <Rows3Icon aria-hidden />, can: () => editor.can().addRowAfter(), command: () => chain().addRowAfter().run() },
      { label: 'Delete row', icon: <Trash2Icon aria-hidden />, can: () => editor.can().deleteRow(), command: () => chain().deleteRow().run() },
      { label: 'Add column before', icon: <Columns3Icon aria-hidden />, can: () => editor.can().addColumnBefore(), command: () => chain().addColumnBefore().run() },
      { label: 'Add column after', icon: <Columns3Icon aria-hidden />, can: () => editor.can().addColumnAfter(), command: () => chain().addColumnAfter().run() },
      { label: 'Delete column', icon: <Trash2Icon aria-hidden />, can: () => editor.can().deleteColumn(), command: () => chain().deleteColumn().run() },
    ] },
    { label: 'Cells', actions: [
      { label: 'Merge selected cells', icon: <MergeIcon aria-hidden />, can: () => editor.can().mergeCells(), command: () => chain().mergeCells().run() },
      { label: 'Split cell', icon: <SplitIcon aria-hidden />, can: () => editor.can().splitCell(), command: () => chain().splitCell().run() },
      { label: 'Toggle header row', icon: <PanelTopIcon aria-hidden />, can: () => editor.can().toggleHeaderRow(), command: () => chain().toggleHeaderRow().run() },
      { label: 'Toggle header column', icon: <PanelLeftIcon aria-hidden />, can: () => editor.can().toggleHeaderColumn(), command: () => chain().toggleHeaderColumn().run() },
    ] },
    { label: 'Table', actions: [
      { label: 'Delete table', icon: <Trash2Icon aria-hidden />, can: () => editor.can().deleteTable(), command: () => chain().deleteTable().run(), destructive: true },
    ] },
  ];
  return <DropdownMenu open={open} onOpenChange={setOpen}>
    <DropdownMenuTrigger asChild><Button type="button" variant={inTable ? 'secondary' : 'ghost'} disabled={unavailable} aria-label={inTable ? 'Table, cursor in a table' : 'Table'}><TableIcon aria-hidden />Table</Button></DropdownMenuTrigger>
    <DropdownMenuContent align="start" className="w-64">
      <DropdownMenuLabel>Insert table</DropdownMenuLabel>
      <p aria-live="polite" className="px-2 pb-1 text-xs text-muted-foreground">{inTable ? 'Tables cannot be inserted inside a table.' : size.rows + ' × ' + size.cols}</p>
      <div role="group" aria-label="Choose table size" className="grid grid-cols-5 gap-1 p-2"
        onKeyDown={event => {
          const offsets: Record<string, number> = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -GRID_SIZE, ArrowDown: GRID_SIZE };
          const offset = offsets[event.key];
          if (offset === undefined) return;
          event.preventDefault(); event.stopPropagation();
          const buttons = Array.from(event.currentTarget.querySelectorAll('button'));
          const index = buttons.indexOf(event.target as HTMLButtonElement);
          buttons[Math.max(0, Math.min(GRID_SIZE * GRID_SIZE - 1, index + offset))]?.focus();
        }}>
        {Array.from({ length: GRID_SIZE * GRID_SIZE }, (_, index) => {
          const rows = Math.floor(index / GRID_SIZE) + 1, cols = index % GRID_SIZE + 1;
          return <Button key={rows + ':' + cols} type="button" variant="outline" size="icon"
            className={cn('size-8 rounded-sm', rows <= size.rows && cols <= size.cols && 'bg-muted border-foreground')}
            aria-label={'Insert ' + rows + ' by ' + cols + ' table'} disabled={unavailable || inTable}
            onPointerEnter={() => setSize({ rows, cols })} onFocus={() => setSize({ rows, cols })}
            onClick={() => run(() => chain().insertTable({ rows, cols, withHeaderRow: true }).run())} />;
        })}
      </div>
      {!inTable && <p className="px-2 pb-1 text-xs text-muted-foreground">Place the cursor in a table to change its rows, columns and cells.</p>}
      {groups.map(group => <React.Fragment key={group.label}>
        <DropdownMenuSeparator /><DropdownMenuLabel>{group.label}</DropdownMenuLabel>
        {group.actions.map(action => <DropdownMenuItem key={action.label} disabled={unavailable || !inTable || !action.can()}
          className={cn(action.destructive && 'text-destructive focus:text-destructive')}
          onSelect={() => run(action.command)}>{action.icon}{action.label}</DropdownMenuItem>)}
      </React.Fragment>)}
    </DropdownMenuContent>
  </DropdownMenu>;
}
